/** Read image File(s) into data URLs with natural dimensions. */
import { notifications } from '@/lib/notifications';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;

/**
 * The ceiling on ONE picture, uploaded by an admin or by a user.
 *
 * A template carries its pictures inside it, as data URLs in the document
 * itself — that is what keeps a saved design self-contained and exportable (see
 * urlToImagePayload). The cost is that every megabyte of photo is a megabyte in
 * every save, every load and every export, and base64 adds a third on top. A
 * 1 MB cap is generous for a passport photo or a logo and keeps templates
 * openable; anything heavier is a picture that wants resizing first.
 */
export const MAX_IMAGE_BYTES = 1024 * 1024;
export const MAX_IMAGE_LABEL = '1 MB';

export function isOversizeImage(file) {
    return !!file && Number(file.size) > MAX_IMAGE_BYTES;
}

/** "1.4 MB" — for telling someone exactly how far over they are. */
export function formatFileSize(bytes) {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/**
 * The refusal for a picture that arrives without a File behind it — a link, a
 * paste from a web page. `code` is there so a caller that swallows the usual
 * cross-origin failures can still tell this one apart and say so out loud.
 */
export function oversizeError(bytes) {
    const err = new Error(
        `That image is ${formatFileSize(bytes)} — pictures must be under ${MAX_IMAGE_LABEL}.`,
    );
    err.code = 'image-too-large';
    return err;
}

/**
 * One toast for a whole rejected batch, naming the files and their sizes.
 *
 * Lives down here rather than in each panel because every route into the canvas
 * — the Add image button, the layer list, a drop, a paste, the user's Replace
 * photo — funnels through the two readers below, and a rule enforced in one of
 * five places is a rule that does not hold.
 */
function warnOversize(files) {
    if (!files.length) return;
    const list = files
        .map((f) => `${f.name || 'image'} (${formatFileSize(f.size)})`)
        .join(', ');
    notifications.show({
        color: 'yellow',
        title: files.length === 1 ? 'Photo is too large' : `${files.length} photos are too large`,
        message: `${list} — each picture must be under ${MAX_IMAGE_LABEL}. `
            + 'Resize or compress it, then try again.',
        autoClose: 6000,
    });
}

/**
 * Layered design files — they belong to the artwork importer
 * (utils/importArtwork.js), which keeps their layer structure.
 *
 * SVG is on this list: an SVG written by Illustrator, Figma, Inkscape or
 * Photoshop carries its layer tree as nested <g> elements exactly the way a
 * .ai or .psd carries its own, so importing one as a single flat picture throws
 * that structure away.
 */
const ARTWORK_EXT = /\.(psd|psb|ai|pdf|tiff?|svg)$/i;
const ARTWORK_MIME = /^(image\/vnd\.adobe\.photoshop|image\/tiff|image\/svg\+xml|application\/pdf|application\/postscript|application\/illustrator)$/i;

/**
 * …of those, the ones a browser can never decode into an <img>. Photoshop
 * reports `image/vnd.adobe.photoshop` and TIFF `image/tiff`, so a naive
 * `type.startsWith('image/')` check swallows them and the <img> then fails to
 * decode. SVG is deliberately NOT here: it imports as layers, but it is also a
 * perfectly good plain picture (a logo dropped onto an image element), so both
 * paths have to stay open to it.
 */
const UNDECODABLE_EXT = /\.(psd|psb|ai|pdf|tiff?)$/i;
const UNDECODABLE_MIME = /^(image\/vnd\.adobe\.photoshop|image\/tiff|application\/pdf|application\/postscript|application\/illustrator)$/i;

export function isArtworkFile(file) {
    if (!file) return false;
    return ARTWORK_EXT.test(file.name || '') || ARTWORK_MIME.test(file.type || '');
}

export function isImageFile(file) {
    if (!file) return false;
    if (UNDECODABLE_EXT.test(file.name || '') || UNDECODABLE_MIME.test(file.type || '')) return false;
    if (file.type?.startsWith('image/')) return true;
    // Some OS/browsers leave MIME empty — fall back to extension
    return IMAGE_EXT.test(file.name || '');
}

export function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

export function measureImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        const done = (width, height) => resolve({
            width: width || 180,
            height: height || 180,
        });
        const timer = setTimeout(() => done(180, 180), 4000);
        img.onload = () => {
            clearTimeout(timer);
            done(img.naturalWidth, img.naturalHeight);
        };
        img.onerror = () => {
            clearTimeout(timer);
            done(180, 180);
        };
        img.src = src;
    });
}

/** Fit image into a max box while keeping aspect ratio. */
export function fitImageSize(naturalW, naturalH, maxSide = 280) {
    const w = Number(naturalW) || 180;
    const h = Number(naturalH) || 180;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    return {
        width: Math.max(24, Math.round(w * scale)),
        height: Math.max(24, Math.round(h * scale)),
    };
}

/**
 * Snapshot FileList immediately — clearing the input empties the live FileList.
 */
export function snapshotFiles(fileList) {
    return Array.from(fileList || []);
}

/**
 * src → the payload shape every caller feeds to addElements().
 * `natural` skips the measuring round-trip when the size is already known.
 */
async function payloadFromSrc(src, { name, maxSide = 280, natural = null } = {}) {
    const measured = natural || await measureImage(src);
    const size = fitImageSize(measured.width, measured.height, maxSide);
    return { src, name: name || 'Image', width: size.width, height: size.height };
}

function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
        reader.readAsDataURL(blob);
    });
}

function nameFromUrl(url) {
    try {
        const path = new URL(url, window.location.href).pathname;
        const file = path.split('/').filter(Boolean).pop() || 'Image';
        return decodeURIComponent(file).replace(/\.[^.]+$/, '') || 'Image';
    } catch {
        return 'Image';
    }
}

/**
 * Fetch an image URL and EMBED it as a data URL, exactly like an uploaded file.
 *
 * Storing the remote URL directly would break exporting: the renderer draws each
 * image onto a canvas and then calls toDataURL(), and a cross-origin image without
 * CORS headers taints that canvas — toDataURL() throws and the whole export fails,
 * not just this one image. Embedding also keeps the template self-contained, so it
 * still renders if the source link later dies.
 */
export async function urlToImagePayload(url, { maxSide = 280 } = {}) {
    const clean = String(url || '').trim();
    if (!clean) throw new Error('Enter an image link.');
    if (/^data:image\//i.test(clean)) return payloadFromSrc(clean, { maxSide });
    if (!/^https?:\/\//i.test(clean)) throw new Error('Link must start with http:// or https://');

    let blob;
    try {
        const res = await fetch(clean, { mode: 'cors' });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        blob = await res.blob();
    } catch {
        // Almost always CORS: the host does not allow this site to read the image.
        throw new Error("Couldn't load that link. The site may block direct access — try downloading the image and uploading it instead.");
    }
    if (!blob.type?.startsWith('image/')) throw new Error('That link is not an image.');
    // Same ceiling as an upload: a linked picture is embedded, so it weighs the
    // template down exactly as a dropped file would.
    if (blob.size > MAX_IMAGE_BYTES) throw oversizeError(blob.size);

    const src = await blobToDataURL(blob);
    return payloadFromSrc(src, { name: nameFromUrl(clean), maxSide });
}

export async function filesToImagePayloads(fileList, { maxSide = 280 } = {}) {
    const files = snapshotFiles(fileList).filter(isImageFile);
    // The oversize ones are dropped, not the whole drop: someone dragging in six
    // photos of which one is huge gets the other five, and is told which failed.
    warnOversize(files.filter(isOversizeImage));
    const out = [];
    for (const file of files.filter((f) => !isOversizeImage(f))) {
        const src = await readFileAsDataURL(file);
        out.push(await payloadFromSrc(src, {
            name: file.name?.replace(/\.[^.]+$/, ''),
            maxSide,
        }));
    }
    return out;
}

/* ------------------------------------------------------------------ *
 *  Clipboard / drag-and-drop images
 * ------------------------------------------------------------------ */

/** Percent-encoded (not base64) so non-ASCII text inside the SVG survives. */
export function svgMarkupToDataUrl(markup) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(String(markup).trim())}`;
}

/**
 * Intrinsic size off the <svg> root — width/height attributes first, viewBox
 * as the fallback. Worth doing by hand: an <img> holding an SVG that only has
 * a viewBox reports the 300×150 default replaced-element size, which would
 * paste every such icon into a box of the wrong shape.
 */
function svgIntrinsicSize(markup) {
    const tag = /<svg\b[^>]*>/i.exec(markup)?.[0];
    if (!tag) return null;
    const attr = (name) => {
        // Leading whitespace required, otherwise `width` also matches stroke-width
        const m = new RegExp(`\\s${name}\\s*=\\s*["']?([^"'\\s>]+)`, 'i').exec(tag);
        if (!m || m[1].includes('%')) return null; // "100%" says nothing about aspect
        const v = parseFloat(m[1]);
        return v > 0 ? v : null;
    };
    const width = attr('width');
    const height = attr('height');
    if (width && height) return { width, height };

    const vb = /\sviewBox\s*=\s*["']\s*[-\d.]+[,\s]+[-\d.]+[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(tag);
    if (!vb) return null;
    const w = parseFloat(vb[1]);
    const h = parseFloat(vb[2]);
    return w > 0 && h > 0 ? { width: w, height: h } : null;
}

function looksLikeSvgMarkup(text) {
    const t = String(text || '').trim();
    if (!t || !/<\/svg\s*>$/i.test(t)) return false;
    // Allow an xml prolog / doctype / comments ahead of the root tag
    return /^(?:<\?xml[^>]*\?>|<!DOCTYPE[^>]*>|<!--[\s\S]*?-->|\s)*<svg[\s>]/i.test(t);
}

/** Something we could fetch and embed. Used where the source already says "image". */
function isFetchableImage(value) {
    const v = String(value || '').trim();
    return /^data:image\//i.test(v) || /^https?:\/\//i.test(v);
}

/**
 * A bare string that is worth treating as an image. Stricter than the above:
 * with nothing but text to go on, only links that name an image file qualify,
 * so pasting an ordinary page link stays a no-op instead of firing a request.
 */
function isImageUrl(value) {
    const v = String(value || '').trim();
    if (/^data:image\//i.test(v)) return true;
    if (!/^https?:\/\//i.test(v)) return false;
    try {
        return IMAGE_EXT.test(new URL(v).pathname);
    } catch {
        return false;
    }
}

function transferData(dataTransfer, type) {
    try {
        return dataTransfer.getData(type) || '';
    } catch {
        return '';
    }
}

/**
 * Design files (PSD/AI/PDF/TIFF) in a drop or paste payload.
 *
 * Synchronous for the same reason as readImageTransfer(): the caller must decide
 * whether to preventDefault() within the event tick, and `dataTransfer` is dead
 * once the handler returns.
 */
export function readArtworkTransfer(dataTransfer) {
    if (!dataTransfer) return [];
    const fromFiles = Array.from(dataTransfer.files || []).filter(isArtworkFile);
    if (fromFiles.length) return fromFiles;
    return Array.from(dataTransfer.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(isArtworkFile);
}

function transferImageFiles(dataTransfer) {
    // `files` and `items` describe the same payload; `items` is the fallback for
    // browsers that leave `files` empty on paste (it is populated for drops).
    const fromFiles = Array.from(dataTransfer.files || []).filter(isImageFile);
    if (fromFiles.length) return fromFiles;
    return Array.from(dataTransfer.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(isImageFile);
}

function imageFromHtml(html) {
    if (!html) return null;
    let parsed;
    // Inert document: parsing loads no resources and runs no script.
    try {
        parsed = new DOMParser().parseFromString(html, 'text/html');
    } catch {
        return null;
    }
    // An <img> tag is itself the promise that the link is an image, so the
    // extension check bare text needs would only reject valid CDN URLs here.
    const img = parsed.querySelector('img[src]');
    const src = img?.getAttribute('src') || '';
    if (isFetchableImage(src)) return { url: src, name: img.getAttribute('alt') || '' };
    const svg = parsed.querySelector('svg');
    if (svg) return { markup: svg.outerHTML };
    return null;
}

/**
 * What image, if any, a paste or drop is carrying.
 *
 * Synchronous on purpose, and it must stay that way: `getData()` only returns
 * anything while the clipboard event is being dispatched, and the caller has to
 * decide whether to preventDefault() in that same tick. Decoding the result is
 * the async half — imageSourceToPayloads().
 *
 * Returns null when there is no image, which is the caller's signal to fall
 * through to its normal paste handling.
 */
export function readImageTransfer(dataTransfer) {
    if (!dataTransfer) return null;

    // A real file (screenshot, "Copy image", a file copied in Finder/Explorer)
    // is the best source available — png, jpg, webp, gif, svg, avif, heic…
    const files = transferImageFiles(dataTransfer);
    if (files.length) return { files };

    // Raw <svg> markup copied out of an editor or a design tool
    const text = transferData(dataTransfer, 'text/plain').trim();
    if (looksLikeSvgMarkup(text)) return { markup: text };

    // Copied from a web page: an <img> to fetch, or an inline <svg> to embed
    const fromHtml = imageFromHtml(transferData(dataTransfer, 'text/html'));
    if (fromHtml) return fromHtml;

    // A bare image link / data URL
    if (isImageUrl(text)) return { url: text };
    return null;
}

/** Decode what readImageTransfer() found into addElements() payloads. */
export async function imageSourceToPayloads(source, { maxSide = 280 } = {}) {
    if (!source) return [];
    if (source.files?.length) return filesToImagePayloads(source.files, { maxSide });
    if (source.markup) {
        // Pasted vector markup never was a file, so it is weighed as text.
        const bytes = new Blob([source.markup]).size;
        if (bytes > MAX_IMAGE_BYTES) throw oversizeError(bytes);
        return [await payloadFromSrc(svgMarkupToDataUrl(source.markup), {
            name: source.name || 'Vector',
            maxSide,
            natural: svgIntrinsicSize(source.markup),
        })];
    }
    if (source.url) return [await urlToImagePayload(source.url, { maxSide })];
    return [];
}
